// Adapted from eval/dashboard/internal_web/src/components/ui/DataTable.tsx (ATM-57): a column may carry responsive classes (`className`). Keep in step until ATM-60 shares it.
/**
 * The columnar table both worklists are printed with.
 *
 * A real `<table>`, not a grid of divs: these are tabular data with headers a screen
 * reader has to announce per cell, and the two worklists share a column vocabulary
 * (condition, model, accuracy, truth → said, triage, status) that only a shared
 * component keeps aligned between the overview's five-row preview and the full page.
 *
 * The chrome is the mock's: the header row a tinted strip ruled above and below, one
 * hairline between rows, and the whole thing printed straight onto the card, as wide as it.
 *
 * A row given an `hrefOf` is clickable across its whole width, and it says so: the tint
 * lands on the `<tr>`, not on the cell that happens to hold the anchor. A table whose
 * rows navigate with no hover affordance reads as static, and the only other cue is the
 * cursor — which nobody sees until they are already on the row.
 *
 * That tint and the header strip run the card's full width, which is why the card's 28px
 * inset lives on the first and last CELL rather than on the wrapper around the table.
 * Padding the wrapper stopped the table — and therefore the tint, the strip and every
 * hairline — where the text starts, so a hovered row read as a band drawn around the words
 * instead of as the row it selects.
 *
 * `caption` is not decoration. A screen reader announces a table by its caption, and
 * without one the nearest heading is all a reviewer gets before six column names.
 */

import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { cn } from "../../lib/cn";
import InfoTip from "./InfoTip";

/** One column: how to head it, how wide, and how to align it. */
export type Column<Row> = {
  /** Stable identity for the React key. */
  id: string;
  header: string;
  /** A definition for the header, shown on a `?` beside it. */
  help?: ReactNode;
  /**
   * A Tailwind width utility, or omitted to take whatever the fixed columns leave.
   *
   * `table-fixed` assigns every specified width first and splits the remainder between the
   * columns that named none — so a column whose content is a fixed-size pill states its
   * width in **pixels** (a percentage of an unknown window cannot fit a 121px chip), and the
   * one prose column that can wrap is left unspecified and absorbs the rest.
   */
  width?: string;
  align?: "left" | "right";
  /**
   * Classes for the column's header and every cell — here, a responsive `hidden lg:table-cell`
   * so a narrow window drops a column instead of scrolling it out of view.
   */
  className?: string;
  cell: (row: Row) => ReactNode;
};

type Props<Row> = {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  /** Stable key per row. */
  keyOf: (row: Row) => string;
  /** Where a row navigates to. Rows are inert when omitted. */
  hrefOf?: (row: Row) => string;
  /** Shown in place of the body when `rows` is empty. */
  empty?: ReactNode;
  /**
   * The table's accessible name, rendered `sr-only`.
   *
   * Names what the ROWS are ("Ingested runs", "Evaluated sessions"), not what the screen
   * is: the card heading above already carries that, and a caption repeating it tells a
   * screen-reader user nothing they were not just told.
   */
  caption?: string;
  /**
   * A Tailwind `min-w-*` for the table, below which the panel scrolls sideways.
   *
   * The guarantee that no cell is ever cut off by a narrow window. Without it the only
   * options at 1000px are a pill overflowing its column — which is what it did, escaping the
   * card's rounded edge — or shrinking chips that carry acuity and review state until their
   * words are clipped. A scrollbar inside the panel is the one outcome that loses nothing.
   *
   * Per table rather than a constant here: the corpus list's columns are all prose and fit
   * anywhere, so forcing them to a worklist's minimum would give that page a scrollbar it
   * has no need of.
   */
  minWidth?: string;
  className?: string;
};

/** A worklist table. */
export default function DataTable<Row>({
  columns,
  rows,
  keyOf,
  hrefOf,
  empty,
  caption,
  minWidth,
  className,
}: Props<Row>) {
  const navigate = useNavigate();
  return (
    // `overflow-x-auto` pairs with `minWidth`: together they turn "the window is too narrow
    // for these columns" into a scrollbar rather than into content leaving the card. It is
    // inert — no scrollbar, no clipping — for any table that fits, which is every table on a
    // laptop-width window.
    <div className={cn("overflow-x-auto pb-2", className)}>
      <table className={cn("w-full table-fixed border-collapse", minWidth)}>
        {caption !== undefined ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cn(
                  "table-head first:pl-7 last:pr-7",
                  column.width,
                  column.className,
                  column.align === "right" ? "text-right" : "text-left",
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  {column.header}
                  {column.help !== undefined ? (
                    <InfoTip label={`What ${column.header} means`}>{column.help}</InfoTip>
                  ) : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="hairline px-0 py-8 text-center">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={keyOf(row)}
                // The row is what a reviewer aims at, so the row is what handles the
                // click — the anchor in cell one is for the keyboard, the middle click and
                // "copy link address", not for the pointer. `onClick` rather than a second
                // overlay: an absolutely positioned pseudo-element inside a `<td>` cannot
                // escape it (see the cell comment below), and a `<tr>` cannot contain an
                // anchor that spans its cells.
                //
                // A plain-text selection inside a cell must not navigate, so a drag that
                // ends with something selected is ignored, and any click that lands on a
                // real control (the anchor, the header `?` tips) is left to it.
                onClick={
                  hrefOf === undefined
                    ? undefined
                    : (event) => {
                        if (event.defaultPrevented) return;
                        if (event.target instanceof Element && event.target.closest("a,button")) {
                          return;
                        }
                        if ((window.getSelection()?.toString() ?? "") !== "") return;
                        navigate(hrefOf(row));
                      }
                }
                className={cn(
                  // The first row's rule is the header strip's own bottom edge.
                  "hairline group transition-colors first:border-t-0",
                  hrefOf !== undefined && "cursor-pointer hover:bg-surface-muted",
                )}
              >
                {columns.map((column, index) => (
                  <td
                    key={column.id}
                    className={cn(
                      "table-cell first:pl-7 last:pr-7",
                      column.className,
                      column.align === "right" ? "text-right" : "text-left",
                    )}
                  >
                    {/* The link wraps the FIRST cell's content only — a single `<a>`
                        around every cell would be invalid inside a table, and one link
                        per cell would announce the row seven times.

                        Its overlay therefore CANNOT reach the other cells: an absolutely
                        positioned `::after` resolves against this `<td>`, so stretching
                        it sideways covered column one plus the gutter and nothing else.
                        The row-wide click comes from the `<tr>`'s own handler below, and
                        the anchor stays the accessible, keyboard-reachable,
                        middle-clickable, copy-link-able target. */}
                    {index === 0 && hrefOf !== undefined ? (
                      <Link to={hrefOf(row)} className="block group-hover:text-ink">
                        {column.cell(row)}
                      </Link>
                    ) : (
                      column.cell(row)
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
